import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import config from '../../../config';
import HeroSearchForm from '../../../forms/HeroSearchForm/HeroSearchForm';
import routeConfiguration from '../../../routeConfiguration';
import useAppSelector from '../../../util/hooks/useAppSelector';
import useAppTheme from '../../../util/hooks/useAppTheme';
import usePass from '../../../util/hooks/usePass';
import useScreenSize from '../../../util/hooks/useScreenSize';
import useSticky from '../../../util/hooks/useSticky';
import { isCordova } from '../../../util/mobile/deviceDetection';
import { createResourceLocatorString } from '../../../util/routes';
import { parse } from '../../../util/urlHelpers';
import FlagButtons from '../../BasicUI/Button/FlagButtons';
import { withBaseComponent } from '../hoc/withBaseComponent';

const heroSearchSx = {
  width: '100%',
  marginLeft: 'auto',
  marginRight: 'auto',
  display: 'flex',
  flexDirection: 'row',
  backgroundColor: 'white',
  alignItems: 'center',
  borderRadius: '50px',
  boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.05)',
  border: '1px solid #d3d3d7',
};

/**
 * Component can be used isolated or with StoryblokSearchResult as default header
 * @param blok.blok
 * @param blok
 * @returns {React.ReactNode}
 * @class
 */
const StoryblokHerosearchPreset = ({ blok }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useAppTheme();
  const [passValid] = usePass();
  const { isDesktop, isMobile } = useScreenSize();
  const yellowBannerState = useAppSelector((state) => state.ui.yellowBannerState);

  const { title: rawTitle, subTitle: rawSubTitle } = blok;

  const title = rawTitle;
  const subTitle = rawSubTitle;

  // Herosearch Scroll Definitions
  const formRef = useRef(null);

  const heroSearchFromHeight = 72;
  const heroSearchFromTopPadding = 5;
  const topBarHeightDesktop = theme.globals.topBarHeightDesktop;
  const annualPassBarHeightDesktop =
    config.locale === intl.locale ? (!passValid && yellowBannerState ? 44 : 0) : 0;
  const annualPassBarHeightMobile = 100;
  const noTopBar = isMobile || isCordova();
  const topBarHeightTotal = !passValid
    ? topBarHeightDesktop + annualPassBarHeightDesktop
    : topBarHeightDesktop;
  const topBarHeightMobile = noTopBar
    ? 0
    : !passValid
      ? !isCordova() && yellowBannerState
        ? annualPassBarHeightMobile
        : 0
      : 0;

  const topDistance = heroSearchFromHeight + heroSearchFromTopPadding;

  const isSticky = useSticky(
    formRef.current,
    isDesktop ? topDistance + topBarHeightTotal : topDistance + topBarHeightMobile
  );

  const [formHeight, setFormHeight] = useState(0);
  const [formWidth, setFormWidth] = useState(1);
  const desktopJumpPosition =
    heroSearchFromHeight + annualPassBarHeightDesktop + heroSearchFromTopPadding;
  const mobileJumpPosition = topBarHeightMobile + heroSearchFromTopPadding;

  useEffect(() => {
    const el = formRef.current;
    if (!el) return;

    const measure = ({ width, height }) => {
      setFormWidth(Math.round(width));
      setFormHeight(Math.round(height));
    };

    // Safari gained ResizeObserver in 13.1, so iOS 12 WebViews still lack it and
    // `new ResizeObserver` threw straight out of this effect, taking the whole
    // Storyblok page down through the error boundary (VANSITE-FRONTEND-4PN).
    // Measuring once on mount keeps the sticky wrapper correctly sized there —
    // it just stops following later resizes. Same guard as SearchMapWithMapbox
    // and NotFoundHero.
    if (typeof ResizeObserver === 'undefined') {
      measure(el.getBoundingClientRect());
      return;
    }

    const observer = new ResizeObserver((entries) => {
      measure(entries[0].contentRect);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { address, origin, bounds } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const handleSubmit = ({ location, dates }) => {
    const searchParams = {
      dates: dates,
      address: location ? location.address : '',
      bounds: location ? location.bounds : '',
    };
    if (location || dates) {
      navigate(
        createResourceLocatorString(
          'SearchPage',
          routeConfiguration(),
          {},
          searchParams,
          intl.locale
        ),
        { state: { tab: 'map' } }
      );
    }
  };

  const locationFieldsPresent = config.sortSearchByDistance
    ? address && origin && bounds
    : address && bounds;
  const initialSearchFormValues = {
    location: locationFieldsPresent
      ? {
          search: address,
          selectedPlace: { address, origin, bounds },
        }
      : null,
  };

  return (
    <Container maxWidth="xl" sx={{ p: 0 }}>
      <Stack spacing={2}>
        {title ? (
          <Typography color="secondary.main" component="h1" textAlign="center" variant="h2">
            {title}
          </Typography>
        ) : null}
        {subTitle ? (
          <Typography color="secondary.main" component="h2" textAlign="center" variant="h5">
            {subTitle}
          </Typography>
        ) : null}
        <Box display="flex" justifyContent="center">
          <Box
            ref={formRef}
            sx={{
              position: 'relative',
              width: { xs: 1, md: 0.7 },
              height: `${heroSearchFromHeight}px`,
            }}
          >
            <Box
              sx={{
                position: isSticky ? 'fixed' : 'relative',
                top: isSticky
                  ? isDesktop
                    ? `${desktopJumpPosition}px`
                    : `${mobileJumpPosition}px`
                  : 'auto',
                zIndex: theme.globals.zIndexStoryblokHerosearchPreset,
                boxShadow: 'none',
                bgcolor: 'none',
                width: formWidth,
              }}
            >
              <HeroSearchForm
                initialValues={initialSearchFormValues}
                onSubmit={handleSubmit}
                sx={heroSearchSx}
              />
            </Box>
            {isSticky ? <Box height={formHeight} /> : null}
          </Box>
        </Box>
        <Box mt={2}>
          <FlagButtons />
        </Box>
      </Stack>
    </Container>
  );
};

export default withBaseComponent(StoryblokHerosearchPreset);
